/**
 * Toast-System — dependency-frei (UX_KONZEPT.md §4 Punkt 3/6, §6 Annahme 4:
 * kein neues UI-Paket in Welle 1, ~30-Zeilen-Eigenlösung erlaubt).
 * Optik nach §6.6: unten rechts, dunkler Grund, farbiger Status-Punkt.
 * API-Form angelehnt an rmnc-energie (Zielstand für den Verbund).
 *
 * Nutzung: <ToastProvider> einmal in App.tsx um die Routen legen, dann
 * überall `const toast = useToast()` und `toast.success("Gespeichert")`
 * bzw. `toast.error("Fehlgeschlagen", { retry: () => refetch() })`.
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react"

type ToastVariant = "success" | "error" | "info"

interface ToastOptions {
  retry?: () => void
}

interface ToastEntry {
  id: number
  message: string
  variant: ToastVariant
  retry?: () => void
}

interface ToastApi {
  success: (message: string, options?: ToastOptions) => void
  error: (message: string, options?: ToastOptions) => void
  info: (message: string, options?: ToastOptions) => void
}

const ToastContext = createContext<ToastApi | null>(null)

const DOT_CLASSES: Record<ToastVariant, string> = {
  success: "bg-green-500",
  error: "bg-red-500",
  info: "bg-slate-400",
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([])
  const nextId = useRef(0)

  const push = useCallback((variant: ToastVariant, message: string, options?: ToastOptions) => {
    const id = nextId.current++
    setToasts((t) => [...t, { id, message, variant, retry: options?.retry }])
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 6000)
  }, [])

  const api: ToastApi = {
    success: (message, options) => push("success", message, options),
    error: (message, options) => push("error", message, options),
    info: (message, options) => push("info", message, options),
  }

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-2"
        aria-live="polite"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className="pointer-events-auto flex items-center gap-3 rounded-lg bg-slate-900 px-4 py-3 text-sm text-white shadow-lg"
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[t.variant]}`} aria-hidden="true" />
            <span className="flex-1">{t.message}</span>
            {t.retry && (
              <button
                type="button"
                onClick={() => {
                  t.retry?.()
                  setToasts((cur) => cur.filter((x) => x.id !== t.id))
                }}
                className="shrink-0 rounded-md border border-slate-600 px-2 py-0.5 text-xs font-medium text-slate-200 hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Erneut versuchen
              </button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error("useToast muss innerhalb von <ToastProvider> verwendet werden")
  return ctx
}

/** Extrahiert eine deutsche Fehlermeldung aus einem axios-Fehler (Muster aller Seiten). */
export function fehlerNachricht(err: unknown, fallback: string): string {
  if (
    typeof err === "object" &&
    err !== null &&
    "response" in err &&
    typeof (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail === "string"
  ) {
    return (err as { response: { data: { detail: string } } }).response.data.detail
  }
  return fallback
}
